import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IOrderVolumeSummaryItem, StepOrderSide } from "../../Interfaces/stepInterfaces";
import { addComma } from "src/helper";
import { addOrEditCompetitionConfig } from "src/Requests/stepRequests";
import { packetErrorHandler } from "src/Utils/requestUtils";
import SimpleTextInput from "src/Components/SimpleTextInput";
import Button from "src/Components/Button";
import "../../Components/CompactOrderPreviewTable.css";

interface IProps {
  values: IOrderVolumeSummaryItem[] | null | undefined;
  editableCompetitionSettings?: boolean;
}

interface ICompetitionSettingsDraft {
  totalActiveOrder: number | null;
  activeOrderPP: number | null;
  totalAmount: number | null;
}

const toDraft = (row: IOrderVolumeSummaryItem): ICompetitionSettingsDraft => ({
  totalActiveOrder: row.totalActiveOrder ?? null,
  activeOrderPP: row.activeOrderPP ?? null,
  totalAmount: row.totalAmount ?? null,
});

const PreviewVolumeSummaryTable = ({ values, editableCompetitionSettings }: IProps) => {
  const [drafts, setDrafts] = useState<Record<string, ICompetitionSettingsDraft>>({});
  // Snapshot of the last-saved values per instrument, used only to detect unsaved edits -
  // the trader can easily change these three fields and forget to click the checkmark, so we
  // need a visible "you changed this but haven't applied it yet" signal.
  const [savedDrafts, setSavedDrafts] = useState<Record<string, ICompetitionSettingsDraft>>({});
  const [savingInstrumentId, setSavingInstrumentId] = useState<string | null>(null);

  useEffect(() => {
    if (!editableCompetitionSettings || !values?.length) return;
    const seeded = Object.fromEntries(
      values
        .filter((row) => row.totalActiveOrder != null)
        .map((row) => [row.instrumentId, toDraft(row)]),
    );
    setDrafts(seeded);
    setSavedDrafts(seeded);
  }, [values, editableCompetitionSettings]);

  const isDirty = (instrumentId: string) => {
    const draft = drafts[instrumentId];
    const saved = savedDrafts[instrumentId];
    if (!draft || !saved) return false;
    return (
      draft.totalActiveOrder !== saved.totalActiveOrder ||
      draft.activeOrderPP !== saved.activeOrderPP ||
      draft.totalAmount !== saved.totalAmount
    );
  };

  if (!values?.length) return null;

  const showSettingsColumns =
    editableCompetitionSettings && values.some((row) => row.totalActiveOrder != null);

  const draftChangeHandler = (
    instrumentId: string,
    field: keyof ICompetitionSettingsDraft,
    value: number | null,
  ) => {
    setDrafts((current) => ({
      ...current,
      [instrumentId]: {
        ...(current[instrumentId] || { totalActiveOrder: null, activeOrderPP: null, totalAmount: null }),
        [field]: value,
      },
    }));
  };

  const saveHandler = (instrumentId: string) => {
    const draft = drafts[instrumentId];
    if (!draft) return;

    setSavingInstrumentId(instrumentId);
    addOrEditCompetitionConfig({
      insCode: instrumentId,
      totalActiveOrder: draft.totalActiveOrder ?? 0,
      activeOrderPP: draft.activeOrderPP ?? 0,
      totalAmount: draft.totalAmount ?? 0,
    })
      .then((resp) => {
        toast.success(resp.msg);
        setSavedDrafts((current) => ({ ...current, [instrumentId]: draft }));
      })
      .catch(packetErrorHandler)
      .finally(() => setSavingInstrumentId(null));
  };

  return (
    <div className="preview-volume-summary-scroll" role="region" aria-label="خلاصه حجم سفارش‌ها" tabIndex={0}>
    <table className="preview-volume-summary-table w-full border-collapse rounded border border-gray-300 bg-gray-50 text-xs">
      <thead>
        <tr>
          <th className="border border-gray-300 px-3 py-2 text-right">#</th>
          <th className="border border-gray-300 px-3 py-2 text-right">جهت</th>
          <th className="border border-gray-300 px-3 py-2 text-right">نماد</th>
          <th className="border border-gray-300 px-3 py-2 text-right">مجموع حجم ارسالی</th>
          <th className="border border-gray-300 px-3 py-2 text-right">سرمایه درگیر (مبلغ)</th>
          {showSettingsColumns ? (
            <>
              <th className="border border-gray-300 px-3 py-2 text-right">حداکثر تعداد سفارش باز</th>
              <th className="border border-gray-300 px-3 py-2 text-right">حداکثر بلوک باز شخص</th>
              <th className="border border-gray-300 px-3 py-2 text-right">حداکثر حجم باز</th>
              <th className="border border-gray-300 px-3 py-2 text-right" />
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {values.map((row, index) => {
          const draft = drafts[row.instrumentId];
          const saved = savedDrafts[row.instrumentId];
          const rowIsEditable = showSettingsColumns && row.totalActiveOrder != null && !!draft;
          const dirty = rowIsEditable && isDirty(row.instrumentId);

          return (
            <tr key={`${row.orderSide}-${row.instrumentId}`}>
              <td className="border border-gray-200 px-3 py-2">{index + 1}</td>
              <td
                className={`border border-gray-200 px-3 py-2 !font-bold ${
                  row.orderSide === StepOrderSide.Buy ? "!text-green-600" : "!text-red-700"
                }`}
              >
                {row.orderSide === StepOrderSide.Buy ? "خرید" : "فروش"}
              </td>
              <td className="border border-gray-200 px-3 py-2">{row.instrumentName}</td>
              <td className="border border-gray-200 px-3 py-2">{addComma(row.totalVolume)}</td>
              <td className="border border-gray-200 px-3 py-2">{addComma(row.engagedCapitalAmount)}</td>
              {showSettingsColumns ? (
                rowIsEditable ? (
                  <>
                    <td className="border border-gray-200 px-2 py-1">
                      <SimpleTextInput
                        name="totalActiveOrder"
                        kind="number"
                        value={
                          draft.totalActiveOrder != null ? addComma(draft.totalActiveOrder) : ""
                        }
                        onChange={(_, value) =>
                          draftChangeHandler(row.instrumentId, "totalActiveOrder", value)
                        }
                        extraText={
                          saved?.totalActiveOrder != null ? addComma(saved.totalActiveOrder) : ""
                        }
                        className="!h-7"
                      />
                    </td>
                    <td className="border border-gray-200 px-2 py-1">
                      <SimpleTextInput
                        name="activeOrderPP"
                        kind="number"
                        value={draft.activeOrderPP != null ? addComma(draft.activeOrderPP) : ""}
                        onChange={(_, value) =>
                          draftChangeHandler(row.instrumentId, "activeOrderPP", value)
                        }
                        extraText={
                          saved?.activeOrderPP != null ? addComma(saved.activeOrderPP) : ""
                        }
                        className="!h-7"
                      />
                    </td>
                    <td className="border border-gray-200 px-2 py-1">
                      <SimpleTextInput
                        name="totalAmount"
                        kind="number"
                        value={draft.totalAmount != null ? addComma(draft.totalAmount) : ""}
                        onChange={(_, value) =>
                          draftChangeHandler(row.instrumentId, "totalAmount", value)
                        }
                        extraText={saved?.totalAmount != null ? addComma(saved.totalAmount) : ""}
                        className="!h-7"
                      />
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-center">
                      <div className="w-16 m-auto">
                        <Button
                          onClick={() => saveHandler(row.instrumentId)}
                          text="ثبت"
                          variant="contained"
                          disabled={savingInstrumentId === row.instrumentId || !dirty}
                          loading={savingInstrumentId === row.instrumentId}
                          className="!h-7 !text-xs"
                        />
                      </div>
                    </td>
                  </>
                ) : (
                  <td className="border border-gray-200 px-3 py-2" colSpan={4} />
                )
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
};

export default PreviewVolumeSummaryTable;
